import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { supabase, TABLES } from '../../supabase/supabase';
import JourneyMapCanvas from '../../components/canvas/JourneyMapCanvas';
import ExperienceMatrixCanvas from '../../components/canvas/ExperienceMatrixCanvas';
import ScreenerFlow from '../../components/screener/ScreenerFlow';

// ── Normalizers ───────────────────────────────────────────────────────────────
const normalizeJourneyPlacements = (rows) =>
  rows.map(p => ({
    id: p.id,
    x_position: p.x_position,
    y_position: p.y_position,
    label: p.metadata?.label || '',
    time_from_previous: p.metadata?.time_from_previous || null,
    order_index: p.metadata?.order_index ?? 0,
  })).sort((a, b) => a.order_index - b.order_index);

const normalizeMatrixPlacements = (rows, cards) =>
  rows.map(p => ({
    id: p.id,
    x_value: p.x_value,
    y_value: p.y_value,
    is_custom: p.is_custom || false,
    touchpoint_card_id: p.touchpoint_card_id || null,
    label: p.is_custom
      ? (p.custom_label || '')
      : (cards.find(c => c.id === p.touchpoint_card_id)?.content || ''),
  }));

// Find lane closest to a given y_position float
const findLane = (lanes, yPosition) =>
  lanes.reduce((best, l) =>
    Math.abs(l.y_position - yPosition) < Math.abs(best.y_position - yPosition) ? l : best
  );

// Get or create a session_modules row.
// IMPORTANT: never use .maybeSingle() here — if duplicate rows ever exist it
// throws, which previously caused a cascade of more duplicate inserts. We always
// pick the earliest existing row, and on an insert race we re-select.
const getOrCreateSessionModule = async (sessionId, moduleId) => {
  const find = async () => {
    const { data } = await supabase
      .from(TABLES.SESSION_MODULES)
      .select('id')
      .eq('session_id', sessionId).eq('module_id', moduleId)
      .order('started_at', { ascending: true })
      .limit(1);
    return data?.[0]?.id ?? null;
  };
  const existing = await find();
  if (existing) return existing;
  const { data: created, error } = await supabase
    .from(TABLES.SESSION_MODULES)
    .insert({ session_id: sessionId, module_id: moduleId, started_at: new Date().toISOString() })
    .select('id').single();
  if (error) return await find(); // lost an insert race — use the row the winner created
  return created?.id ?? null;
};

const ParticipantSession = () => {
  const { participantToken } = useParams();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [session, setSession] = useState(null);
  const [screenerQuestions, setScreenerQuestions] = useState(null);
  const [screenerDone, setScreenerDone] = useState(false);
  const [activeModule, setActiveModule] = useState(null);

  // Journey map state
  const [lanes, setLanes] = useState([]);
  const [journeyPlacements, setJourneyPlacements] = useState([]);

  // Experience matrix state
  const [cards, setCards] = useState([]);
  const [matrixPlacements, setMatrixPlacements] = useState([]);

  // Refs for stable polling closures
  const sessionRef = useRef(null);
  const activeModuleIdRef = useRef(null);
  const activeModuleTypeRef = useRef(null);
  const sessionModuleIdRef = useRef(null);
  const lanesRef = useRef([]);
  const journeyPlacementsRef = useRef([]);
  const cardsRef = useRef([]);
  const matrixPlacementsRef = useRef([]);

  useEffect(() => { lanesRef.current = lanes; }, [lanes]);
  useEffect(() => { journeyPlacementsRef.current = journeyPlacements; }, [journeyPlacements]);
  useEffect(() => { cardsRef.current = cards; }, [cards]);
  useEffect(() => { matrixPlacementsRef.current = matrixPlacements; }, [matrixPlacements]);

  // ── Initial session + screener load ──────────────────────────────────────
  useEffect(() => {
    const fetchSession = async () => {
      const { data } = await supabase
        .from(TABLES.SESSIONS).select('id, status, project_id')
        .eq('participant_token', participantToken).maybeSingle();

      if (!data) { setNotFound(true); setLoading(false); return; }
      sessionRef.current = data;
      setSession(data);

      const { data: questions } = await supabase
        .from(TABLES.SCREENER_QUESTIONS)
        .select('id, question_text, question_type, options, is_required, order_index, tag_key')
        .eq('project_id', data.project_id).order('order_index');

      const { data: existingResponses } = await supabase
        .from(TABLES.SCREENER_RESPONSES).select('id').eq('session_id', data.id).limit(1);

      const alreadyDone = existingResponses && existingResponses.length > 0;
      const hasQuestions = questions && questions.length > 0;
      setScreenerQuestions(hasQuestions && !alreadyDone ? questions : []);
      setScreenerDone(alreadyDone || !hasQuestions);
      setLoading(false);
    };
    fetchSession();
  }, [participantToken]);

  // ── Load a module (branches by type) ──────────────────────────────────────
  const loadModule = async (moduleId) => {
    activeModuleIdRef.current = moduleId;

    const { data: mod } = await supabase
      .from(TABLES.MODULES).select('*').eq('id', moduleId).single();
    if (!mod) return;

    activeModuleTypeRef.current = mod.type;
    setActiveModule(mod);

    const smId = await getOrCreateSessionModule(sessionRef.current.id, moduleId);
    sessionModuleIdRef.current = smId;

    if (mod.type === 'journey_map') {
      const [{ data: lanesData }, { data: placementsData }] = await Promise.all([
        supabase.from(TABLES.JOURNEY_MAP_LANES).select('*').eq('module_id', moduleId),
        supabase.from(TABLES.JOURNEY_MAP_PLACEMENTS).select('*').eq('session_module_id', smId),
      ]);
      setLanes(lanesData || []);
      lanesRef.current = lanesData || [];
      setJourneyPlacements(normalizeJourneyPlacements(placementsData || []));

    } else if (mod.type === 'xy_plot') {
      const [{ data: cardData }, { data: placementsData }] = await Promise.all([
        supabase.from(TABLES.TOUCHPOINT_CARDS).select('*').eq('module_id', moduleId).order('order_index'),
        supabase.from(TABLES.XY_PLOT_PLACEMENTS).select('*').eq('session_module_id', smId),
      ]);
      setCards(cardData || []);
      cardsRef.current = cardData || [];
      setMatrixPlacements(normalizeMatrixPlacements(placementsData || [], cardData || []));
    }
  };

  // ── Poll active_session_state every second ────────────────────────────────
  useEffect(() => {
    if (!session) return;
    const poll = async () => {
      const [sessResult, stateResult] = await Promise.all([
        supabase.from(TABLES.SESSIONS).select('status').eq('id', sessionRef.current.id).single(),
        supabase.from(TABLES.ACTIVE_SESSION_STATE)
          .select('current_module_id, updated_at').eq('session_id', sessionRef.current.id).maybeSingle(),
      ]);
      const { data: sess } = sessResult;
      const { data: state } = stateResult;

      if (sess?.status === 'complete') {
        setSession(prev => ({ ...prev, status: 'complete' }));
        return;
      }
      if (!state?.current_module_id) return;
      if (state.current_module_id !== activeModuleIdRef.current) {
        loadModule(state.current_module_id);
      }
    };
    const interval = setInterval(poll, 1000);
    return () => clearInterval(interval);
  }, [session]);

  // ── Screener submit ───────────────────────────────────────────────────────
  const handleScreenerComplete = async ({ answers }) => {
    const sid = sessionRef.current.id;
    const responseRows = Object.entries(answers).map(([questionId, responseValue]) =>
      ({ session_id: sid, question_id: questionId, response_value: responseValue })
    );
    if (responseRows.length > 0) await supabase.from(TABLES.SCREENER_RESPONSES).insert(responseRows);

    // segment_tags has a NOT NULL `name` column — must be provided or the insert
    // fails silently. Use the screener option's human label for the display name.
    const tagRows = (screenerQuestions || [])
      .filter(q => q.tag_key && answers[q.id] !== undefined)
      .map(q => {
        const value = answers[q.id];
        const opt = (q.options || []).find(o => o.value === value);
        const label = opt?.label || value;
        return {
          session_id: sid,
          project_id: sessionRef.current.project_id || null,
          tag_key: q.tag_key,
          tag_value: value,
          name: `${q.tag_key}: ${label}`,
        };
      });
    if (tagRows.length > 0) {
      const { error: tagErr } = await supabase.from(TABLES.SEGMENT_TAGS).insert(tagRows);
      if (tagErr) console.error('[screener] segment_tags insert failed:', tagErr);
    }

    setScreenerDone(true);
  };

  // ── Bump updated_at ───────────────────────────────────────────────────────
  const bumpState = async () => {
    await supabase.from(TABLES.ACTIVE_SESSION_STATE)
      .update({ updated_at: new Date().toISOString() })
      .eq('session_id', sessionRef.current.id);
  };

  // ── Journey map handlers ──────────────────────────────────────────────────
  const handleAddMilestone = async ({ x_position, y_position, time_from_previous, label }) => {
    const smId = sessionModuleIdRef.current;
    if (!smId) return;
    const currentLanes = lanesRef.current;
    const order_index = journeyPlacementsRef.current.length;
    const lane = currentLanes.length > 0 ? findLane(currentLanes, y_position) : null;

    const { data, error } = await supabase.from(TABLES.JOURNEY_MAP_PLACEMENTS)
      .insert([{ session_module_id: smId, lane_id: lane?.id || null, x_position, y_position,
        metadata: { label, time_from_previous, order_index } }])
      .select().single();
    if (error) { console.error('[Participant] add milestone error:', error); return; }

    setJourneyPlacements(prev => normalizeJourneyPlacements([
      ...prev.map(p => ({ id: p.id, x_position: p.x_position, y_position: p.y_position,
        metadata: { label: p.label, time_from_previous: p.time_from_previous, order_index: p.order_index } })),
      data,
    ]));
    await bumpState();
  };

  const handleMoveMilestone = async ({ id, x_position, y_position, time_from_previous }) => {
    const lane = lanesRef.current.length > 0 ? findLane(lanesRef.current, y_position) : null;
    const existing = journeyPlacementsRef.current.find(p => p.id === id);
    const metadata = { label: existing?.label || '', order_index: existing?.order_index ?? 0, time_from_previous };
    const { error } = await supabase.from(TABLES.JOURNEY_MAP_PLACEMENTS)
      .update({ x_position, y_position, lane_id: lane?.id || null, metadata }).eq('id', id);
    if (error) { console.error('[Participant] move milestone error:', error); return; }
    setJourneyPlacements(prev => prev.map(p => p.id === id ? { ...p, x_position, y_position, time_from_previous } : p));
    await bumpState();
  };

  const handleUpdateLabel = async ({ id, label }) => {
    const existing = journeyPlacementsRef.current.find(p => p.id === id);
    const metadata = { label, order_index: existing?.order_index ?? 0, time_from_previous: existing?.time_from_previous || null };
    const { error } = await supabase.from(TABLES.JOURNEY_MAP_PLACEMENTS).update({ metadata }).eq('id', id);
    if (error) { console.error('[Participant] label update error:', error); return; }
    setJourneyPlacements(prev => prev.map(p => p.id === id ? { ...p, label } : p));
    await bumpState();
  };

  // ── Experience matrix handlers ────────────────────────────────────────────
  const handlePlaceCard = async ({ cardId, x, y }) => {
    const smId = sessionModuleIdRef.current;
    if (!smId) return;
    const { data, error } = await supabase.from(TABLES.XY_PLOT_PLACEMENTS)
      .insert([{ session_module_id: smId, touchpoint_card_id: cardId, x_value: x, y_value: y, is_custom: false }])
      .select().single();
    if (error) { console.error('[Participant] place card error:', error); return; }
    setMatrixPlacements(prev => normalizeMatrixPlacements([...prev.map(p => ({
      ...p, custom_label: p.is_custom ? p.label : undefined,
    })), data], cardsRef.current));
    await bumpState();
  };

  const handleMoveCard = async ({ placementId, x, y }) => {
    // Optimistic update before the DB round-trip so the card snaps immediately
    setMatrixPlacements(prev => prev.map(p => p.id === placementId ? { ...p, x_value: x, y_value: y } : p));
    const { error } = await supabase.from(TABLES.XY_PLOT_PLACEMENTS)
      .update({ x_value: x, y_value: y }).eq('id', placementId);
    if (error) { console.error('[Participant] move card error:', error); return; }
    await bumpState();
  };

  const handleRemoveCard = async (placementId) => {
    const { error } = await supabase.from(TABLES.XY_PLOT_PLACEMENTS).delete().eq('id', placementId);
    if (error) { console.error('[Participant] remove card error:', error); return; }
    setMatrixPlacements(prev => prev.filter(p => p.id !== placementId));
    await bumpState();
  };

  const handleAddCustomCard = async ({ label, x, y }) => {
    const smId = sessionModuleIdRef.current;
    if (!smId) return;
    const { data, error } = await supabase.from(TABLES.XY_PLOT_PLACEMENTS)
      .insert([{ session_module_id: smId, custom_label: label, x_value: x, y_value: y, is_custom: true }])
      .select().single();
    if (error) { console.error('[Participant] add custom card error:', error); return; }
    setMatrixPlacements(prev => [...prev, {
      id: data.id, x_value: data.x_value, y_value: data.y_value,
      is_custom: true, touchpoint_card_id: null, label,
    }]);
    await bumpState();
  };

  // ── Shared header ─────────────────────────────────────────────────────────
  const ModuleHeader = ({ mod }) => (
    <div className="h-12 bg-white border-b border-gray-100 flex items-center px-5 gap-3 shrink-0 shadow-sm">
      <div className="w-6 h-6 rounded-full bg-rutabaga-pink/10 flex items-center justify-center shrink-0">
        <span className="text-rutabaga-pink text-xs font-black">R</span>
      </div>
      <span className="font-black text-rutabaga-dark text-sm">{mod.name}</span>
      {mod.config?.instructions && (
        <span className="text-gray-400 text-xs font-medium ml-1 truncate">— {mod.config.instructions}</span>
      )}
    </div>
  );

  // ── Render states ─────────────────────────────────────────────────────────
  if (loading) return (
    <div className="h-screen w-full flex items-center justify-center bg-rutabaga-bg">
      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-rutabaga-pink" />
    </div>
  );

  if (notFound) return (
    <div className="h-screen w-full flex flex-col items-center justify-center bg-rutabaga-bg text-center px-6">
      <div className="w-16 h-16 rounded-full bg-white border border-gray-200 flex items-center justify-center text-3xl mb-6 shadow-sm">🔗</div>
      <h1 className="text-2xl font-black text-rutabaga-dark tracking-tight">Session not found</h1>
      <p className="text-gray-500 mt-3 max-w-sm">This link may be invalid or has been removed. Check with your interviewer.</p>
    </div>
  );

  if (session?.status === 'complete') return (
    <div className="h-screen w-full flex flex-col items-center justify-center bg-rutabaga-bg text-center px-6">
      <div className="w-16 h-16 rounded-full bg-white border border-gray-200 flex items-center justify-center text-3xl mb-6 shadow-sm">✅</div>
      <h1 className="text-2xl font-black text-rutabaga-dark tracking-tight">Session complete</h1>
      <p className="text-gray-500 mt-3 max-w-sm">Thank you for your participation!</p>
    </div>
  );

  if (!screenerDone && screenerQuestions && screenerQuestions.length > 0) return (
    <ScreenerFlow questions={screenerQuestions} onComplete={handleScreenerComplete} />
  );

  if (!activeModule) return (
    <div className="h-screen w-full flex flex-col items-center justify-center bg-rutabaga-bg text-center px-6">
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-10 max-w-md w-full">
        <div className="w-12 h-12 rounded-full bg-rutabaga-pink/10 flex items-center justify-center mx-auto mb-5">
          <span className="text-rutabaga-pink text-xl font-black">R</span>
        </div>
        <h1 className="text-xl font-black text-rutabaga-dark tracking-tight">Welcome to your session</h1>
        <p className="text-gray-500 text-sm mt-2 mb-6">Your interviewer will start the session shortly.</p>
        <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
          <span className="flex h-2 w-2 rounded-full bg-green-400 animate-pulse" />
          Waiting for interviewer…
        </div>
      </div>
    </div>
  );

  if (activeModule.type === 'journey_map') return (
    <div className="h-screen w-full flex flex-col bg-white overflow-hidden">
      <ModuleHeader mod={activeModule} />
      <div className="flex-1 flex flex-col items-stretch justify-center">
        <JourneyMapCanvas
          milestones={journeyPlacements}
          lanes={lanes}
          onAddMilestone={handleAddMilestone}
          onMoveMilestone={handleMoveMilestone}
          onUpdateLabel={handleUpdateLabel}
        />
        <p className="text-center text-[10px] text-gray-300 font-bold uppercase tracking-widest py-3">
          Click to place milestones · Drag to reposition · Double-click label to rename
        </p>
      </div>
    </div>
  );

  if (activeModule.type === 'xy_plot') return (
    <div className="h-screen w-full flex flex-col bg-white overflow-hidden">
      <ModuleHeader mod={activeModule} />
      <div className="flex-1 overflow-hidden">
        <ExperienceMatrixCanvas
          cards={cards}
          placements={matrixPlacements}
          config={activeModule.config || {}}
          onPlaceCard={handlePlaceCard}
          onMoveCard={handleMoveCard}
          onRemoveCard={handleRemoveCard}
          onAddCustomCard={handleAddCustomCard}
        />
      </div>
    </div>
  );

  return (
    <div className="h-screen w-full flex items-center justify-center bg-rutabaga-bg">
      <p className="text-gray-400 font-medium">This module type is not yet supported.</p>
    </div>
  );
};

export default ParticipantSession;
